<template>
  <div class="page">
    <h1 class="title">Data Link</h1>
    <p class="subtitle">Cette page permet de lier les comptes Discord/Twitch aux joueurs d'un serveur. Affiche tous les joueurs vus durant les 7 derniers jours (données de la base de données).</p>

    <!-- Help Section -->
    <div class="card help-card">
      <div class="help-header" @click="showHelp = !showHelp">
        <h2>📖 Mode d'emploi</h2>
        <span class="toggle">{{ showHelp ? '▼' : '▶' }}</span>
      </div>
      
      <div v-if="showHelp" class="help-content">
        <div class="help-section">
          <h3>🎯 Code couleur des joueurs</h3>
          <ul>
            <li><span class="color-badge red"></span> <strong>Rouge</strong> : Aucun compte Discord ni Twitch lié (priorité haute)</li>
            <li><span class="color-badge orange"></span> <strong>Orange</strong> : Un seul compte lié (Discord OU Twitch manquant)</li>
            <li><span class="color-badge neutral"></span> <strong>Gris</strong> : Profil complet (Discord ET Twitch liés)</li>
          </ul>
        </div>

        <div class="help-section">
          <h3>🔗 Comment lier les comptes ?</h3>
          
          <div class="method">
            <h4>Méthode 1 : Clic simple (recommandé pour débuter)</h4>
            <ol>
              <li>Cliquez sur un <strong>joueur</strong> dans la colonne de gauche (il sera surligné en orange)</li>
              <li>Cliquez sur un compte <strong>Discord</strong> (colonne centrale) ou <strong>Twitch</strong> (colonne droite)</li>
              <li>Confirmez la liaison dans la popup</li>
            </ol>
          </div>

          <div class="method">
            <h4>Méthode 2 : Glisser-Déposer (Drag & Drop)</h4>
            <p>Glissez une carte et déposez-la sur une autre pour les lier :</p>
            <ul>
              <li>Discord → Joueur in-game</li>
              <li>Twitch → Joueur in-game</li>
              <li>Discord → Twitch (sans joueur)</li>
              <li>Joueur → Discord</li>
              <li>Joueur → Twitch</li>
            </ul>
          </div>
        </div>

        <div class="help-section">
          <h3>📊 Les 3 colonnes</h3>
          <ul>
            <li><strong>Gauche</strong> : Tous les joueurs vus sur le serveur (7 derniers jours)</li>
            <li><strong>Centre</strong> : Comptes Discord sans Twitch ET sans joueur lié</li>
            <li><strong>Droite</strong> : Comptes sans Discord lié</li>
          </ul>
        </div>

        <div class="help-section">
          <h3>💡 Astuces</h3>
          <ul>
            <li>Utilisez les <strong>barres de recherche</strong> pour filtrer rapidement</li>
            <li>Traitez d'abord les joueurs <span class="color-badge red"></span> <strong>rouges</strong>, puis les <span class="color-badge orange"></span> <strong>oranges</strong></li>
            <li>Les données se mettent à jour <strong>automatiquement</strong> après chaque liaison</li>
            <li>Les comptes disparaissent des colonnes centrale/droite une fois complètement liés</li>
            <li>Utilisez le bouton <strong>🧹 Nettoyer les doublons</strong> pour supprimer les entrées en double (garde celles avec le plus d'informations)</li>
          </ul>
        </div>

        <div class="help-section">
          <h3>❓ Questions fréquentes</h3>
          <details>
            <summary>Pourquoi un joueur apparaît alors qu'il n'est plus connecté ?</summary>
            <p>L'interface affiche tous les joueurs vus durant les <strong>7 derniers jours</strong>, pas seulement ceux actuellement en ligne. Cela permet de rattraper les liens manquants même si le joueur est déconnecté.</p>
          </details>
          <details>
            <summary>Puis-je lier un Discord à un Twitch sans avoir de joueur in-game ?</summary>
            <p><strong>Oui !</strong> Glissez-déposez entre les colonnes centrale et droite pour lier directement Discord ↔ Twitch.</p>
          </details>
          <details>
            <summary>Que faire si je lie un mauvais compte ?</summary>
            <p>Faites simplement le bon lien : le nouveau lien écrasera l'ancien. Pour les cas complexes, contactez un administrateur.</p>
          </details>
          <details>
            <summary>Comment fonctionne le nettoyage des doublons ?</summary>
            <p>Le système analyse tous les joueurs avec le même nom et calcule un <strong>score d'information</strong> pour chacun. Les entrées avec PersonId lié (Discord/Twitch) et plus d'identifiants obtiennent un score plus élevé. Seule l'entrée avec le meilleur score est conservée, les autres sont supprimées.</p>
          </details>
        </div>
      </div>
    </div>

    <div class="card">
      <h2>Sélection du serveur FiveM</h2>

      <div class="row">
        <label for="serverSelect">Serveur</label>
        <select id="serverSelect" v-model="selectedServerCode" :disabled="loading">
          <option value="" disabled>Choisir un serveur...</option>
          <option v-for="s in servers" :key="s.buddiCode" :value="s.buddiCode">
            {{ s.name }} ({{ s.buddiCode }})
          </option>
        </select>
      </div>

      <div v-if="loading" class="hint">Chargement des serveurs...</div>
      <div v-if="error" class="error">{{ error }}</div>

      <div v-if="selectedServerCode" class="row">
        <button class="primary" @click="refreshPlayers" :disabled="playersLoading">
          {{ playersLoading ? 'Actualisation...' : 'Actualiser la liste' }}
        </button>
        <button class="secondary" @click="cleanupDuplicates" :disabled="cleanupLoading">
          {{ cleanupLoading ? 'Nettoyage...' : '🧹 Nettoyer les doublons' }}
        </button>
      </div>
      <div v-if="cleanupMessage" :class="cleanupMessage.type === 'success' ? 'success-msg' : 'error'">
        {{ cleanupMessage.text }}
      </div>
    </div>

    <div class="columns" v-if="selectedServerCode">
      <!-- Col 1: In-game players -->
      <div class="col">
        <div class="card">
          <h2>Joueurs récents (7 jours)</h2>
          <div class="filter"><input type="text" v-model="playersQuery" placeholder="Filtrer joueurs (nom, discord, twitch)..." /></div>
          <div v-if="playersError" class="error">{{ playersError }}</div>
          <div v-else-if="playersLoading" class="hint">Chargement des joueurs...</div>
          <div v-else>
            <ul class="players">
              <li v-if="filteredPlayers.length === 0" class="placeholder">Aucun joueur trouvé.</li>
              <li
                v-for="p in filteredPlayers"
                :key="p.name"
                class="player-card"
                :class="[cardStatusClass(p), { clickable: true, selected: selectedInGame && selectedInGame.name === p.name }]"
                @click="onSelectInGame(p)"
                draggable="true"
                @dragstart="onDragStart('player', p)"
                @dragover.prevent
                @drop="onDropOnPlayer(p)"
              >
                <div class="top">
                  <div class="name">{{ p.name || 'Inconnu' }}</div>
                  <div class="id" v-if="p.inGameId">#{{ p.inGameId }}</div>
                </div>
                <div class="details">
                  <div class="line"><span class="label">Vu:</span> <span class="value">{{ formatLastSeen(p.lastSeen) }}</span></div>
                  <div class="line"><span class="label">Discord:</span> <span class="value">{{ p.discordId || '-' }}</span></div>
                  <div class="line"><span class="label">Twitch:</span> <span class="value">{{ p.twitchName || '-' }}</span></div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Col 2: Incomplete Discord users -->
      <div class="col">
        <div class="card">
          <h2>Discord sans Twitch/Joueur</h2>
          <div class="filter"><input type="text" v-model="discordQuery" placeholder="Filtrer Discord (username, id)..." /></div>
          <div v-if="incompleteDiscordLoading" class="hint">Chargement...</div>
          <div v-else-if="incompleteDiscordError" class="error">{{ incompleteDiscordError }}</div>
          <div v-else>
            <ul class="players">
              <li v-if="filteredIncompleteDiscord.length === 0" class="placeholder">Aucun utilisateur dans la liste.</li>
              <li v-for="d in filteredIncompleteDiscord" :key="d.personId" class="player-card clickable"
                  @click="onSelectDiscord(d)"
                  draggable="true"
                  @dragstart="onDragStart('discord', d)"
                  @dragover.prevent
                  @drop="onDropOnDiscord(d)">
                <div class="top">
                  <div class="name">{{ d.discordUsername || d.discordId || 'Discord inconnu' }}</div>
                  <div class="id">#{{ d.personId }}</div>
                </div>
                <div class="details">
                  <div class="line"><span class="label">Discord ID:</span> <span class="value">{{ d.discordId || '-' }}</span></div>
                  <div class="line"><span class="label">Twitch:</span> <span class="value">-</span></div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Col 3: Incomplete Twitch names -->
      <div class="col">
        <div class="card">
          <h2>Comptes sans Discord</h2>
          <div class="filter"><input type="text" v-model="twitchQuery" placeholder="Filtrer Twitch (display/twitch)..." /></div>
          <div v-if="incompleteTwitchLoading" class="hint">Chargement...</div>
          <div v-else-if="incompleteTwitchError" class="error">{{ incompleteTwitchError }}</div>
          <div v-else>
            <ul class="players">
              <li v-if="filteredIncompleteTwitch.length === 0" class="placeholder">Aucun pseudo dans la liste.</li>
              <li v-for="t in filteredIncompleteTwitch" :key="t.personId" class="player-card clickable"
                  @click="onSelectTwitch(t)"
                  draggable="true"
                  @dragstart="onDragStart('twitch', t)"
                  @dragover.prevent
                  @drop="onDropOnTwitch(t)">
                <div class="top">
                  <div class="name">{{ t.displayName || 'Twitch inconnu' }}</div>
                  <div class="id">{{ t.twitchName || '-' }}</div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { api } from '../services/api'

const showHelp = ref(false)

const servers = ref([])
const selectedServerCode = ref('')
const loading = ref(false)
const error = ref('')

const players = ref([])
const playersLoading = ref(false)
const playersError = ref('')

const cleanupLoading = ref(false)
const cleanupMessage = ref(null)

// Incomplete Discord/Twitch lists
const incompleteDiscord = ref([])
const incompleteDiscordLoading = ref(false)
const incompleteDiscordError = ref('')
const incompleteTwitch = ref([])
const incompleteTwitchLoading = ref(false)
const incompleteTwitchError = ref('')

// Selection for linking
const selectedInGame = ref(null)

// Filters
const playersQuery = ref('')
const discordQuery = ref('')
const twitchQuery = ref('')

// Computed filtered lists
const filteredPlayers = computed(() => {
  const q = playersQuery.value.trim().toLowerCase()
  if (!q) return players.value
  return players.value.filter(p => {
    const fields = [p.name, p.discordId, p.twitchName]
    return fields.some(f => (f || '').toString().toLowerCase().includes(q))
  })
})

const filteredIncompleteDiscord = computed(() => {
  const q = discordQuery.value.trim().toLowerCase()
  if (!q) return incompleteDiscord.value
  return incompleteDiscord.value.filter(d => {
    const fields = [d.discordUsername, d.discordId, String(d.personId)]
    return fields.some(f => (f || '').toString().toLowerCase().includes(q))
  })
})

const filteredIncompleteTwitch = computed(() => {
  const q = twitchQuery.value.trim().toLowerCase()
  if (!q) return incompleteTwitch.value
  return incompleteTwitch.value.filter(t => {
    const fields = [t.displayName, t.twitchName, String(t.personId)]
    return fields.some(f => (f || '').toString().toLowerCase().includes(q))
  })
})

// Drag & Drop linking state/handlers
const dragData = ref(null) // { type: 'player'|'discord'|'twitch', item }

const onDragStart = (type, item) => {
  dragData.value = { type, item }
}

const onDropOnPlayer = async (player) => {
  if (!dragData.value) return
  try {
    if (dragData.value.type === 'discord') {
      await api.post('/TwitchWatch/linkDiscordToPlayer', {
        serverCode: selectedServerCode.value,
        playerName: player.name,
        discordId: dragData.value.item.discordId
      })
    } else if (dragData.value.type === 'twitch') {
      await api.post('/TwitchWatch/linkTwitchToPlayer', {
        serverCode: selectedServerCode.value,
        playerName: player.name,
        twitchName: dragData.value.item.twitchName || dragData.value.item.displayName
      })
    }
    await Promise.all([loadPlayers(), loadIncompleteDiscord(), loadIncompleteTwitch()])
  } catch (e) {
    console.error(e)
    alert('Erreur lors du lien via glisser-déposer vers Joueur.')
  } finally {
    dragData.value = null
  }
}

const linkDiscordToTwitch = async (discordId, twitchName) => {
  await api.post('/TwitchWatch/linkDiscordToTwitch', { discordId, twitchName })
}

const onDropOnDiscord = async (discordItem) => {
  if (!dragData.value) return
  try {
    if (dragData.value.type === 'player') {
      await api.post('/TwitchWatch/linkDiscordToPlayer', {
        serverCode: selectedServerCode.value,
        playerName: dragData.value.item.name,
        discordId: discordItem.discordId
      })
    } else if (dragData.value.type === 'twitch') {
      await linkDiscordToTwitch(discordItem.discordId, dragData.value.item.twitchName || dragData.value.item.displayName)
    }
    await Promise.all([loadPlayers(), loadIncompleteDiscord(), loadIncompleteTwitch()])
  } catch (e) {
    console.error(e)
    alert('Erreur lors du lien via glisser-déposer vers Discord.')
  } finally {
    dragData.value = null
  }
}

const onDropOnTwitch = async (twitchItem) => {
  if (!dragData.value) return
  try {
    if (dragData.value.type === 'player') {
      await api.post('/TwitchWatch/linkTwitchToPlayer', {
        serverCode: selectedServerCode.value,
        playerName: dragData.value.item.name,
        twitchName: twitchItem.twitchName || twitchItem.displayName
      })
    } else if (dragData.value.type === 'discord') {
      await linkDiscordToTwitch(dragData.value.item.discordId, twitchItem.twitchName || twitchItem.displayName)
    }
    await Promise.all([loadPlayers(), loadIncompleteDiscord(), loadIncompleteTwitch()])
  } catch (e) {
    console.error(e)
    alert('Erreur lors du lien via glisser-déposer vers Twitch.')
  } finally {
    dragData.value = null
  }
}

const loadServers = async () => {
  try {
    loading.value = true
    error.value = ''
    const { data } = await api.get('/TwitchWatch/fiveMServers')
    if (data?.success) {
      servers.value = data.servers || []
      // Pré-sélectionner le premier serveur si dispo
      if (!selectedServerCode.value && servers.value.length > 0) {
        selectedServerCode.value = servers.value[0].buddiCode
      }
    } else {
      error.value = 'Impossible de charger la liste des serveurs.'
    }
  } catch (e) {
    console.error(e)
    error.value = 'Erreur lors du chargement des serveurs.'
  } finally {
    loading.value = false
  }
}

const loadPlayers = async () => {
  if (!selectedServerCode.value) return
  try {
    playersLoading.value = true
    playersError.value = ''
    const { data } = await api.get('/TwitchWatch/currentPlayers', {
      params: { serverCode: selectedServerCode.value }
    })
    if (data?.success) {
      // Sort by status (red first, then orange, then green), then by lastSeen (most recent first)
      const list = Array.isArray(data.players) ? data.players : []
      players.value = list.sort((a, b) => {
        const statusA = getStatusPriority(a)
        const statusB = getStatusPriority(b)
        if (statusA !== statusB) return statusA - statusB
        // Sort by lastSeen descending (most recent first)
        const dateA = new Date(a.lastSeen || 0)
        const dateB = new Date(b.lastSeen || 0)
        return dateB - dateA
      })
    } else {
      players.value = []
      playersError.value = 'Impossible de charger les joueurs en ligne.'
    }
  } catch (e) {
    console.error(e)
    players.value = []
    playersError.value = 'Erreur lors du chargement des joueurs.'
  } finally {
    playersLoading.value = false
  }
}

const refreshPlayers = () => {
  loadPlayers()
}

const cleanupDuplicates = async () => {
  if (!selectedServerCode.value) return
  if (!confirm('Voulez-vous nettoyer les doublons pour ce serveur ? Les entrées avec moins d\'informations seront supprimées.')) return
  
  try {
    cleanupLoading.value = true
    cleanupMessage.value = null
    const { data } = await api.post('/TwitchWatch/cleanupDuplicates', null, {
      params: { serverCode: selectedServerCode.value }
    })
    if (data?.success) {
      cleanupMessage.value = {
        type: 'success',
        text: `✅ ${data.deletedCount} doublon(s) supprimé(s)`
      }
      // Refresh the player list
      await loadPlayers()
    } else {
      cleanupMessage.value = {
        type: 'error',
        text: 'Erreur lors du nettoyage'
      }
    }
  } catch (e) {
    console.error(e)
    cleanupMessage.value = {
      type: 'error',
      text: 'Erreur lors du nettoyage des doublons'
    }
  } finally {
    cleanupLoading.value = false
    // Clear message after 5 seconds
    setTimeout(() => {
      cleanupMessage.value = null
    }, 5000)
  }
}

const loadIncompleteDiscord = async () => {
  if (!selectedServerCode.value) return
  try {
    incompleteDiscordLoading.value = true
    incompleteDiscordError.value = ''
    const { data } = await api.get('/TwitchWatch/incomplete/discord', { params: { serverCode: selectedServerCode.value } })
    if (data?.success) {
      incompleteDiscord.value = Array.isArray(data.items) ? data.items : []
    } else {
      incompleteDiscord.value = []
      incompleteDiscordError.value = 'Impossible de charger les Discord incomplets.'
    }
  } catch (e) {
    console.error(e)
    incompleteDiscord.value = []
    incompleteDiscordError.value = 'Erreur lors du chargement des Discord incomplets.'
  } finally {
    incompleteDiscordLoading.value = false
  }
}

const loadIncompleteTwitch = async () => {
  if (!selectedServerCode.value) return
  try {
    incompleteTwitchLoading.value = true
    incompleteTwitchError.value = ''
    const { data } = await api.get('/TwitchWatch/incomplete/twitch', { params: { serverCode: selectedServerCode.value } })
    if (data?.success) {
      incompleteTwitch.value = Array.isArray(data.items) ? data.items : []
    } else {
      incompleteTwitch.value = []
      incompleteTwitchError.value = 'Impossible de charger les Twitch incomplets.'
    }
  } catch (e) {
    console.error(e)
    incompleteTwitch.value = []
    incompleteTwitchError.value = 'Erreur lors du chargement des Twitch incomplets.'
  } finally {
    incompleteTwitchLoading.value = false
  }
}

const onSelectInGame = (p) => {
  selectedInGame.value = p
}

const onSelectDiscord = async (d) => {
  if (!selectedInGame.value) {
    alert('Sélectionnez d\'abord un joueur en jeu dans la colonne de gauche.')
    return
  }
  if (!confirm(`Lier le joueur "${selectedInGame.value.name || 'Inconnu'}" au Discord ${d.discordId}?`)) return
  try {
    await api.post('/TwitchWatch/linkDiscordToPlayer', {
      serverCode: selectedServerCode.value,
      playerName: selectedInGame.value.name,
      discordId: d.discordId
    })
    await Promise.all([loadPlayers(), loadIncompleteDiscord(), loadIncompleteTwitch()])
  } catch (e) {
    console.error(e)
    alert('Erreur lors du lien Discord → joueur. L\'endpoint est-il disponible?')
  }
}

const onSelectTwitch = async (t) => {
  if (!selectedInGame.value) {
    alert('Sélectionnez d\'abord un joueur en jeu dans la colonne de gauche.')
    return
  }
  const label = t.twitchName || t.displayName || 'Twitch inconnu'
  if (!confirm(`Lier le joueur "${selectedInGame.value.name || 'Inconnu'}" au Twitch ${label}?`)) return
  try {
    await api.post('/TwitchWatch/linkTwitchToPlayer', {
      serverCode: selectedServerCode.value,
      playerName: selectedInGame.value.name,
      twitchName: t.twitchName || t.displayName
    })
    await Promise.all([loadPlayers(), loadIncompleteDiscord(), loadIncompleteTwitch()])
  } catch (e) {
    console.error(e)
    alert('Erreur lors du lien Twitch → joueur. L\'endpoint est-il disponible?')
  }
}

watch(selectedServerCode, async (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    selectedInGame.value = null
    await Promise.all([loadPlayers(), loadIncompleteDiscord(), loadIncompleteTwitch()])
  }
})

onMounted(async () => {
  await loadServers()
  if (selectedServerCode.value) {
    await Promise.all([loadPlayers(), loadIncompleteDiscord(), loadIncompleteTwitch()])
  }
})

const formatLastSeen = (iso) => {
  if (!iso) return '-'
  try {
    // Parse UTC time from backend
    const dt = new Date(iso)
    
    // Calculate difference in UTC (Date.now() is in UTC)
    const diffMs = Date.now() - dt.getTime()
    const sec = Math.floor(diffMs / 1000)
    
    if (sec < 0) return 'à l\'instant'
    if (sec < 60) return `il y a ${sec}s`
    const min = Math.floor(sec / 60)
    if (min < 60) return `il y a ${min}m`
    const hrs = Math.floor(min / 60)
    if (hrs < 24) return `il y a ${hrs}h`
    const days = Math.floor(hrs / 24)
    return `il y a ${days}j`
  } catch {
    return iso
  }
}

const getStatusPriority = (p) => {
  const hasDiscord = !!p?.discordId
  const hasTwitch = !!p?.twitchName
  if (!hasDiscord && !hasTwitch) return 0 // Red - highest priority
  if (!hasDiscord || !hasTwitch) return 1 // Orange - medium priority
  return 2 // Green/Complete - lowest priority
}

const cardStatusClass = (p) => {
  const hasDiscord = !!p?.discordId
  const hasTwitch = !!p?.twitchName
  if (!hasDiscord && !hasTwitch) return 'status-missing-both'
  if (!hasDiscord || !hasTwitch) return 'status-missing-one'
  return ''
}
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.title {
  font-size: 24px;
  font-weight: 700;
}

.subtitle {
  color: #A0AEC0;
}

.row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0;
}

label {
  min-width: 80px;
}

.primary {
  background: #eda526;
  color: #0b0f1a;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
}

.primary:hover:not(:disabled) {
  background: #f5b136;
}

.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.secondary {
  background: rgba(1, 110, 126, 0.2);
  color: #9ee7f2;
  border: 1px solid rgba(1, 110, 126, 0.5);
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
}

.secondary:hover:not(:disabled) {
  background: rgba(1, 110, 126, 0.3);
  border-color: rgba(1, 110, 126, 0.7);
}

.secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.success-msg {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.1);
  border: 1px solid rgba(74, 222, 128, 0.3);
  padding: 8px 12px;
  border-radius: 8px;
  margin-top: 8px;
}

.hint {
  color: #A0AEC0;
}

.players {
  margin: 0;
  padding-left: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 65vh; /* make lists scroll inside each column */
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.placeholder {
  color: #A0AEC0;
  list-style: none;
}

.player-card {
  list-style: none;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 10px;
}

.player-card.clickable { cursor: pointer; }

.player-card.selected {
  border-color: rgba(237, 165, 38, 0.9);
  box-shadow: 0 0 0 2px rgba(237, 165, 38, 0.35);
}

.player-card.status-missing-both {
  background: rgba(255, 60, 60, 0.35);
  border-color: rgba(255, 60, 60, 0.8);
}

.player-card.status-missing-one {
  background: rgba(255, 165, 0, 0.35);
  border-color: rgba(255, 165, 0, 0.8);
}

.top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.name {
  font-weight: 600;
}

.id {
  font-size: 12px;
  opacity: 0.85;
}

.badges {
  display: flex;
  gap: 6px;
}

.badge.id {
  background: rgba(1,110,126,0.2);
  border-color: rgba(1,110,126,0.4);
  color: #9ee7f2;
}

.details .line {
  margin: 4px 0;
}

.identifiers {
  margin: 6px 0 0 0;
  padding-left: 18px;
}

.filter {
  margin-bottom: 8px;
}

.filter input {
  width: 100%;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255,255,255,0.2);
  color: white;
  padding: 6px 8px;
  border-radius: 8px;
  outline: none;
}/* 3-column layout */
.columns {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.col {
  min-width: 0; /* prevent overflow in grid items */
}/* Responsive: stack columns on small screens */
@media (max-width: 1024px) {
  .columns {
    grid-template-columns: 1fr;
  }
}/* Help Section Styles */
.help-card {
  background: rgba(237, 165, 38, 0.08);
  border-color: rgba(237, 165, 38, 0.3);
}

.help-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.help-header:hover {
  opacity: 0.85;
}

.help-header h2 {
  margin: 0;
  font-size: 18px;
}

.help-content {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.help-section {
  margin-bottom: 20px;
}

.help-section:last-child {
  margin-bottom: 0;
}

.help-section h3 {
  font-size: 16px;
  margin-bottom: 10px;
  color: #eda526;
}

.help-section h4 {
  font-size: 14px;
  margin: 12px 0 8px 0;
  color: #fff;
}

.help-section ul, .help-section ol {
  margin: 8px 0;
  padding-left: 24px;
}

.help-section li {
  margin: 6px 0;
  line-height: 1.5;
}

.help-section p {
  margin: 8px 0;
  line-height: 1.6;
  color: #cbd5e0;
}

.method {
  background: rgba(255, 255, 255, 0.03);
  padding: 12px;
  border-radius: 8px;
  margin: 12px 0;
  border-left: 3px solid rgba(237, 165, 38, 0.5);
}

.color-badge {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 6px;
  vertical-align: middle;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.color-badge.red {
  background: rgba(255, 60, 60, 0.5);
  border-color: rgba(255, 60, 60, 0.8);
}

.color-badge.orange {
  background: rgba(255, 165, 0, 0.5);
  border-color: rgba(255, 165, 0, 0.8);
}

.color-badge.neutral {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

details {
  background: rgba(255, 255, 255, 0.03);
  padding: 10px;
  border-radius: 6px;
  margin: 8px 0;
  border-left: 2px solid rgba(1, 110, 126, 0.5);
}

details summary {
  cursor: pointer;
  font-weight: 600;
  color: #9ee7f2;
  user-select: none;
}

details summary:hover {
  color: #b8f0fa;
}

details p {
  margin-top: 8px;
  padding-left: 8px;
  color: #cbd5e0;
}
</style>
